import React, { useEffect, useState } from 'react'
import { useRewards } from '@/hooks/useRewards'
import { RewardSetupCard, RewardConditionsCard, SegmentsCard, ProductsCard, DigitalCodesCard } from './ui-components'
import { REWARD_TYPES } from '@/utils/constants/rewardsConstants'
import { fetchAndEnhanceProducts } from './ui-components/utils'

/**
 * RewardSetup component displays the setup step for creating a reward.
 * Contains three cards: Reward Setup, Reward Conditions, and Segments.
 *
 * @returns {JSX.Element} The rendered RewardSetup component.
 */
const RewardSetup = () => {
  const {
    formData,
    validation,
    rewardTypes,
    rewardCardTypes,
    segments,
    rewardTypesLoading,
    segmentsLoading,
    bonusProductsLoading,
    rewardCardTypesLoading,
    pepVouchersOrProductsLoading,
    handleFormChange,
    handleFetchedProductsUpdate,
    handleValidation,
    resetRewardConditions,
    validateName,
    validatePoints,
    validateOrderQuantity,
    validateBonusProductLimit,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validateValue,
    validateDigitalValue,
    validateDigitalPoints,
    validateDigitalSupplier,
    validateLoyaltyURL,
  } = useRewards()

  // Local state for validation errors
  const [nameError, setNameError] = useState('')
  const [pointsError, setPointsError] = useState('')
  const [orderQuantityError, setOrderQuantityError] = useState('')
  const [bonusProductLimitError, setBonusProductLimitError] = useState('')
  const [redeemPointsError, setRedeemPointsError] = useState('')
  const [rewardCashAmountError, setRewardCashAmountError] = useState('')
  const [rewardCardTypeError, setRewardCardTypeError] = useState('')
  const [valueError, setValueError] = useState('')
  // Digital-specific errors
  const [digitalValueError, setDigitalValueError] = useState('')
  const [digitalPointsError, setDigitalPointsError] = useState('')
  const [digitalSupplierError, setDigitalSupplierError] = useState('')
  // Loyalty-specific errors
  const [loyaltyURLError, setLoyaltyURLError] = useState('')

  // Local state for product fetching
  const [productsLoading, setProductsLoading] = useState(false)



  // Effect to fetch products when selectedProducts changes
  useEffect(() => {
    fetchAndEnhanceProducts(
      formData.selectedProducts,
      handleFetchedProductsUpdate,
      setProductsLoading,
      () => {}, // Empty function for error handling since we're not using it
      formData.fetchedProducts // Pass existing fetched products to preserve details
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedProducts, handleFetchedProductsUpdate])

  // Helper function to validate a single field
  const validateField = (isValid, formValue, validator, setError) => {
    if (isValid) {
      const validationError = validator(formValue)
      setError(validationError || '')
    }
  }

  // Effect to validate fields when validation state changes (e.g., when Next is clicked)
  useEffect(() => {
    validateField(validation.nameValid, formData.name, validateName, setNameError)
    validateField(validation.pointsValid, formData.points, validatePoints, setPointsError)
    validateField(validation.orderQuantityValid, formData.orderQuantity, validateOrderQuantity, setOrderQuantityError)
    validateField(validation.bonusProductLimitValid, formData.bonusProductLimit, validateBonusProductLimit, setBonusProductLimitError)
    validateField(validation.redeemPointsValid, formData.redeemPoints, validateReedemPoints, setRedeemPointsError)
    validateField(validation.rewardCashAmountValid, formData.rewardCashAmount, validateRewardCashAmount, setRewardCashAmountError)
    validateField(validation.rewardCardTypeValid, formData.rewardCardType, validateRewardCardType, setRewardCardTypeError)
    validateField(validation.valueValid, formData.value, validateValue, setValueError)

    // Digital specific inline validation when flags are set
    validateField(validation.digitalValueValid, formData.digitalValue, validateDigitalValue, setDigitalValueError)
    validateField(validation.digitalPointsValid, formData.digitalPoints, validateDigitalPoints, setDigitalPointsError)
    validateField(validation.digitalSupplierValid, formData.digitalSupplier, validateDigitalSupplier, setDigitalSupplierError)
    // Loyalty specific inline validation when flags are set
    validateField(validation.loyaltyURLValid, formData.loyaltyURL, validateLoyaltyURL, setLoyaltyURLError)
  }, [
    validation.nameValid,
    validation.pointsValid,
    validation.orderQuantityValid,
    validation.bonusProductLimitValid,
    validation.redeemPointsValid,
    validation.rewardCashAmountValid,
    validation.rewardCardTypeValid,
    validation.valueValid,
    formData.name,
    formData.points,
    formData.orderQuantity,
    formData.bonusProductLimit,
    formData.redeemPoints,
    formData.rewardCashAmount,
    formData.rewardCardType,
    formData.value,
    validateName,
    validatePoints,
    validateOrderQuantity,
    validateBonusProductLimit,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validateValue,
    validateDigitalValue,
    validateDigitalPoints,
    validateDigitalSupplier,
    validation.digitalValueValid,
    validation.digitalPointsValid,
    validation.digitalSupplierValid,
    formData.digitalValue,
    formData.digitalPoints,
    formData.digitalSupplier,
    validateLoyaltyURL,
    validation.loyaltyURLValid,
    formData.loyaltyURL
  ])

  const handleNameChange = (value) => {
    const validationError = validateName(value)
    setNameError(validationError || '')

    if (validationError) {
      handleFormChange('name', value)
      handleValidation('name', true)
    } else {
      handleFormChange('name', value)
      handleValidation('name', false)
    }
  }

  const handleResetFormDataFields = (fields) => {
    fields.forEach(({ field, value, validate }) => {
      handleFormChange(field, value);
      handleValidation(field, validate);
    });
  }

   const rewardsAttributesReset = [
      { field: 'quota', value: '', validate: false },
      { field: 'leftoverInventory', value: '', validate: false },
      { field: 'limitPerStore', value: '', validate: false },
      { field: 'quotaDigitalCard', value: '', validate: false },
      { field: 'leftoverInventoryDigitalCard', value: '', validate: false },
      { field: 'limitPerStoreDigitalCard', value: '', validate: false },
      { field: 'quotaPhysicalCard', value: '', validate: false },
      { field: 'leftoverInventoryPhysicalCard', value: '', validate: false },
      { field: 'limitPerStorePhysicalCard', value: '', validate: false },
      {field : 'unlimitedInventory', value: false, validate: false},
    ];

  const handleRewardTypeChange = (value) => {
  if(!value || value?.length === 0) return;
  const selectedValue = value?.[0]?.value || ''
  const isBonusProducts = selectedValue === REWARD_TYPES.BONUS_PRODUCTS

  // Update reward type and validation
  handleFormChange('rewardType', selectedValue)
  handleValidation('rewardType', selectedValue.length === 0)

  // Clear error states
  setPointsError('')
  setOrderQuantityError('')
  setBonusProductLimitError('')
  setValueError('')

  // Common reset fields
  const commonResets = [
    { field: 'points', value: '', validate: false },
    { field: 'selectedProducts', value: [], validate: false },
    { field: 'selectedPepVouchersProducts', value: [], validate: false },
    { field: 'rewardCardType', value: '', validate: false },
    { field: 'rewardCashAmount', value: '', validate: false },
    { field: 'redeemPoints', value: '', validate: false },
    { field: 'value', value: '', validate: false },
    {...rewardsAttributesReset}
  ]

  // Additional resets only if not bonus products
  const nonBonusResets = [
    { field: 'orderQuantity', value: '', validate: false },
    { field: 'bonusProductLimit', value: '', validate: false },
  ]

  const fieldsToReset = isBonusProducts ? commonResets : [...commonResets, ...nonBonusResets]

  handleResetFormDataFields(fieldsToReset);
}


  const handlePointsChange = (value) => {
    const validationError = validatePoints(value)
    setPointsError(validationError || '')

    if (validationError) {
      handleFormChange('points', value)
      handleValidation('points', true)
    } else {
      handleFormChange('points', value)
      handleValidation('points', false)
    }
  }

  // Digital specific handlers
  const handleDigitalValueChange = (value) => {
    const err = (!value || Number(value) <= 0) ? 'Enter a valid amount' : ''
    setDigitalValueError(err)
    handleFormChange('digitalValue', value)
    handleValidation('digitalValue', Boolean(err))
  }

  const handleDigitalSupplierChange = (value) => {
    handleFormChange('digitalSupplier', value)
    const err = (!value || !String(value).trim()) ? 'Supplier is required' : ''
    setDigitalSupplierError(err)
    handleValidation('digitalSupplier', Boolean(err))
  }

  const handleDigitalPointsChange = (value) => {
    const n = Number(value)
    const err = (!Number.isFinite(n) || n <= 0) ? 'Enter valid points' : ''
    setDigitalPointsError(err)
    handleFormChange('digitalPoints', value)
    handleValidation('digitalPoints', Boolean(err))
  }

  const handleLoyaltyURLChange = (value) => {
    const validationError = validateLoyaltyURL(value)
    setLoyaltyURLError(validationError || '')

    handleFormChange('loyaltyURL', value)
    handleValidation('loyaltyURL', Boolean(validationError))
  }

  const handleCouponBasedLoyaltyRewardChange = (value) => {
    handleFormChange('couponBasedLoyaltyReward', value)
  }

  const handleAllCustomersChange = (value) => {
    const err = (typeof value !== 'boolean') ? 'Value must be true or false' : ''
    handleFormChange('selectedSegments', [])
    handleFormChange('allCustomers', value)
    handleValidation('allCustomers', Boolean(err))
  }

  const handleExcludeSegmentsChange = (value) => {
    const err = (typeof value !== 'boolean') ? 'Value must be true or false' : ''
    handleFormChange('excludedSegments', [])
    handleFormChange('excludeSegments', value)
    handleValidation('excludeSegments', Boolean(err))
  }

  const handleSegmentsChange = (value) => {
    // value is expected to be an array of groups, each a list of ids: [[id,id], [id]]
    const groupedIds = Array.isArray(value) ? value : []
    handleFormChange('selectedSegments', groupedIds)
  }

  // New: persist excluded segments (array of ids)
  const handleExcludedSegmentsChange = (value) => {
    const selectedValues = value?.map(item => item.value) || []
    handleFormChange('excludedSegments', selectedValues)
  }

  const handleProductsChange = (value) => {
    // Store the full product objects instead of just IDs for better display
    const selectedProducts = value || []
    const selectedProductIds = selectedProducts.map(item => item.value)

    handleFormChange('selectedProducts', selectedProducts)
    handleValidation('selectedProducts', selectedProductIds.length === 0)
  }

  const handlePepVouchersChange = (value) => {
    // Store the full product objects instead of just IDs for better display
    const selectedPepVouchersProducts = value || []
    const selectedProductIds = selectedPepVouchersProducts.map(item => item.value)
    handleFormChange('selectedPepVouchersProducts', selectedPepVouchersProducts)
    handleValidation('selectedPepVouchersProducts', selectedProductIds.length === 0)
  }

  const handleOrderQuantityChange = (value) => {
    const validationError = validateOrderQuantity(value)
    setOrderQuantityError(validationError || '')

    if (validationError) {
      handleFormChange('orderQuantity', value)
      handleValidation('orderQuantity', true)
    } else {
      handleFormChange('orderQuantity', value)
      handleValidation('orderQuantity', false)
    }
  }

  const handleRedeemPointsChange = (value) => {
    const validationError = validateReedemPoints(value)
    setRedeemPointsError(validationError || '')

    if (validationError) {
      handleFormChange('redeemPoints', value)
      handleValidation('redeemPoints', true)
    } else {
      handleFormChange('redeemPoints', value)
      handleValidation('redeemPoints', false)
    }
  }

  const handleRewardCashAmountChange = (value) => {
    const validationError = validateRewardCashAmount(value)
    setRewardCashAmountError(validationError || '')

    if (validationError) {
      handleFormChange('rewardCashAmount', value)
      handleValidation('rewardCashAmount', true)
    } else {
      handleFormChange('rewardCashAmount', value)
      handleValidation('rewardCashAmount', false)
    }
  }

  const handleRewardCardTypeChange = (value) => {
    const validationError = validateRewardCardType(value[0]?.value)
    setRewardCardTypeError(validationError || '')

    if (validationError) {
      handleFormChange('rewardCardType', value[0]?.value)
      handleValidation('rewardCardType', true)
    } else {
      handleFormChange('rewardCardType', value[0]?.value)
      handleValidation('rewardCardType', false)
    }
    handleResetFormDataFields(rewardsAttributesReset);
  }

  const handleBonusProductLimitChange = (value) => {
    const validationError = validateBonusProductLimit(value)
    setBonusProductLimitError(validationError || '')

    if (validationError) {
      handleFormChange('bonusProductLimit', value)
      handleValidation('bonusProductLimit', true)
    } else {
      handleFormChange('bonusProductLimit', value)
      handleValidation('bonusProductLimit', false)
    }
  }

  const handleValueChange = (value) => {
    const validationError = validateValue(value)
    setValueError(validationError || '')

    if (validationError) {
      handleFormChange('value', value)
      handleValidation('value', true)
    } else {
      handleFormChange('value', value)
      handleValidation('value', false)
    }
  }

  const handleResetRewardConditions = () => {
    // Call the original reset function
    resetRewardConditions()

    // Clear all local error states
    setPointsError('')
    setOrderQuantityError('')
    setBonusProductLimitError('')
    setRedeemPointsError('')
    setRewardCardTypeError('')
    setRewardCashAmountError('')
    setValueError('')
    setDigitalValueError('')
    setDigitalPointsError('')
    setDigitalSupplierError('')
    setLoyaltyURLError('')
  }

  // Handle products update from ProductsCard
  const handleUpdateProducts = (updatedProducts) => {
    handleFetchedProductsUpdate(updatedProducts)
  }

  // Handle product deletion
  const handleDeleteProduct = (productId) => {
    // Remove from selected products
    const updatedSelectedProducts = formData.selectedProducts.filter(
      product => product.value !== productId
    )
    handleFormChange('selectedProducts', updatedSelectedProducts)

    // Remove from fetched products while preserving details for remaining products
    const updatedFetchedProducts = formData.fetchedProducts.filter(
      product => product.productId !== productId
    )
    handleFetchedProductsUpdate(updatedFetchedProducts)
  }

  const showProductsCard = formData.selectedProducts && formData.selectedProducts.length > 0 && formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS
  const showDigitalCodesCard = formData?.rewardType === REWARD_TYPES.DIGITAL || 
                                formData?.rewardType === REWARD_TYPES.DIGITAL_PRE_ORDER ||
                                (formData?.rewardType === REWARD_TYPES.LOYALTY && formData?.couponBasedLoyaltyReward === true)

  return (
    <div className="rewardSetup-container flex flex-col gap-6">
      <RewardSetupCard
        formData={formData}
        handleNameChange={handleNameChange}
        nameError={nameError}
      />

      <RewardConditionsCard
        formData={formData}
        validation={validation}
        rewardTypes={rewardTypes}
        rewardCardTypes={rewardCardTypes}
        rewardTypesLoading={rewardTypesLoading}
        bonusProductsLoading={bonusProductsLoading}
        rewardCardTypesLoading={rewardCardTypesLoading}
        pepVouchersOrProductsLoading={pepVouchersOrProductsLoading}
        handleRewardTypeChange={handleRewardTypeChange}
        handlePointsChange={handlePointsChange}
        handleProductsChange={handleProductsChange}
        handleOrderQuantityChange={handleOrderQuantityChange}
        handleRedeemPointsChange={handleRedeemPointsChange}
        handleRewardCashAmountChange={handleRewardCashAmountChange}
        handleRewardCardTypeChange={handleRewardCardTypeChange}
        handleDigitalValueChange={handleDigitalValueChange}
        handleDigitalSupplierChange={handleDigitalSupplierChange}
        handleDigitalPointsChange={handleDigitalPointsChange}
        handleBonusProductLimitChange={handleBonusProductLimitChange}
        handleValueChange={handleValueChange}
        handleLoyaltyURLChange={handleLoyaltyURLChange}
        handleCouponBasedLoyaltyRewardChange={handleCouponBasedLoyaltyRewardChange}
        resetRewardConditions={handleResetRewardConditions}
        handlePepVouchersChange={handlePepVouchersChange}
        pointsError={pointsError}
        orderQuantityError={orderQuantityError}
        bonusProductLimitError={bonusProductLimitError}
        rewardCashAmountError={rewardCashAmountError}
        redeemPointsError={redeemPointsError}
        digitalValueError={digitalValueError}
        digitalSupplierError={digitalSupplierError}
        digitalPointsError={digitalPointsError}
        rewardCardTypeError={rewardCardTypeError}
        valueError={valueError}
        loyaltyURLError={loyaltyURLError}
      />


      {/* Products Card - Show when we have selected products */}
      {showProductsCard && (
        <ProductsCard
          products={formData.fetchedProducts}
          productsLoading={productsLoading}
          onUpdateProducts={handleUpdateProducts}
          onDeleteProduct={handleDeleteProduct}
        />
      )}

      {showDigitalCodesCard && (
        <DigitalCodesCard />
      )}

      <SegmentsCard
        formData={formData}
        segments={segments}
        segmentsLoading={segmentsLoading}
        handleAllCustomersChange={handleAllCustomersChange}
        handleExcludeSegmentsChange={handleExcludeSegmentsChange}
        handleSegmentsChange={handleSegmentsChange}
        handleExcludedSegmentsChange={handleExcludedSegmentsChange}
      />

    </div>
  )
}

export default RewardSetup
